import * as echarts from 'echarts';
import { emptyColumnText, chartThemeColors } from '@/constant';
import { formatChartData, fetchStatisticValKey } from '@/lib/utils';
import { services } from '@/service';
import { sortBy } from 'lodash';

export default {
  data() {
    return {
      chartEl: null,
      tooltipFormatter: undefined,
      chartLoading: false,
      chartTextColor: '#3d465a',
      relationStatisticItem: null,
      curThemeColors: []
    };
  },
  props: {
    chartTitle: {
      type: String,
      default: ''
    },
    byDashboard: {
      type: Boolean,
      default: false
    },
    byDownload: {
      type: Boolean,
      default: false
    },
    customLegend: {
      type: Boolean,
      default: false
    },
    chartHeight: {
      type: Number,
      default: 0
    }
  },
  beforeDestroy() {
    if (this.chartEl) {
      this.chartEl.off('mouseover', this.xAxisOver);
      this.chartEl.off('mouseout', this.xAxisout);
      this.chartEl.off('click', this.chartTapHandle);
      this.chartEl.clear();
      this.chartEl.dispose();
      this.chartEl = null;
    }
  },
  methods: {
    async refresh(params, options) {
      const preRelationStatisticItem = this.relationStatisticItem;
      let dataSet = {};
      try {
        this.chartLoading = true;

        const { haveOriginData = false } = options || {};

        if (haveOriginData) {
          dataSet = params.originData;
        } else {
          let originData = {};
          if (this.chartType === 'tableAnalysisChart') {
            originData = await this.getDimensionTable(params);
          } else {
            originData = await this.getDimension(params);
          }
          params.originData = originData;
          dataSet = originData;
        }

        this.relationStatisticItem = { value: params.relationFilterVal, statisticName: params.relationFilterName };

        if (this.chartType !== 'tableAnalysisChart' && params.originData && params.originData.rowsData) {
          params.originData.rowsData = this.sortDataHandle(params, this.relationStatisticItem);
        }

        if (!['tableAnalysisChart', 'tableChart'].includes(this.chartType)) {
          params.originData = formatChartData(
            {
              rows: [...(params.rows || [])],
              columns: [...(params.columns || [])],
              statistics: [...(params.statistics || [])],
              chartConfig: { ...(params.chartConfig || {}) },
              relationFilterVal: params.relationFilterVal,
              relationFilterName: params.relationFilterName
            },
            params.originData,
            this.chartType,
            { initLeftRight: !this.byDashboard }
          );
        }

        // 组件内部数据初始化
        const comData = await this.initDataHandle(params);

        if (!comData.isChart) {
          return { dataSet };
        }

        // 图表组件初始化ECharts及渲染
        if (!this.chartEl) {
          this.chartEl = echarts.init(this.$refs.chartRef, null, { devicePixelRatio: 2 });
          this.chartEl.on('mouseover', this.xAxisOver);
          this.chartEl.on('mouseout', this.xAxisout);
          this.chartEl.on('click', this.chartTapHandle);
        }

        if (this.chartTitle) {
          comData.config.title = {
            text: this.chartTitle,
            left: 'center',
            textStyle: { fontSize: 24, color: this.chartTextColor }
          };
        }

        if (this.byDownload) {
          let fontSize = params.originData.chartConfig.fontSize || 12;
          fontSize = Math.ceil(fontSize * 1.5);

          if (['barChart', 'barXChart', 'lineChart', 'lineChart2'].includes(this.chartType)) {
            comData.config.xAxis.forEach(xItem => {
              xItem.axisLabel.fontSize = fontSize;
              xItem.nameTextStyle.fontSize = fontSize;
            });
            comData.config.yAxis.forEach(yItem => {
              yItem.axisLabel.fontSize = fontSize;
              yItem.nameTextStyle.fontSize = fontSize;
            });
            comData.config.series = comData.config.series.map(serieItem => {
              return { ...serieItem, label: { ...serieItem.label, fontSize } };
            });
          }
          if (['pieChart', 'pieChart2'].includes(this.chartType)) {
            comData.config.label.fontSize = fontSize;
            comData.config.emphasis.label.fontSize = fontSize;
          }
        }

        if (comData.config && comData.config.tooltip && comData.config.tooltip.formatter) {
          this.tooltipFormatter = comData.config.tooltip.formatter;
        } else {
          this.tooltipFormatter = undefined;
        }

        if ((params.colors || []).length > 0) {
          comData.config.color = this.fillColorHandle([...(params.colors || chartThemeColors[0])]);
        }

        this.curThemeColors = comData.config.color || [];

        this.setOptionHandle(comData.config);

        return { dataSet, defaultChartConfig: params.originData.chartConfig };
      } catch (err) {
        this.relationStatisticItem = preRelationStatisticItem;
        console.error(err);
        // 页面跳转后导致echarts无法渲染的报错
        if (err.message.includes('Initialize failed: invalid dom.')) {
          return { dataSet };
        }

        if (err.message.includes("Cannot read property 'getAttribute' of undefined")) {
          return { dataSet };
        }
        this.$error(err.message || 'error： chart render');
      } finally {
        this.chartLoading = false;
      }
    },
    fillColorHandle(colors) {
      const newColors = [...colors];
      for (let i = 0; i < 10; i++) {
        newColors.push(...colors);
      }
      if (newColors.length >= 90) {
        return newColors;
      }
      return this.fillColorHandle(newColors);
    },
    mergeDataSourceHandle(courseCondition, researchCondition) {
      return { query: [...JSON.parse(courseCondition || '[]'), ...JSON.parse(researchCondition || '[]')] };
    },
    async getDimension({
      rows,
      columns,
      statistics,
      relationFilterVal,
      relationFilterName,
      courseId,
      courseCondition,
      researchCondition
    }) {
      const dataSource = this.mergeDataSourceHandle(courseCondition, researchCondition);
      return await services.research.getDimension(
        { rows, columns, statistics, relationFilterVal, relationFilterName, dataSource },
        courseId
      );
    },
    async getDimensionTable({
      rows,
      columns,
      relationFilterVal,
      relationFilterName,
      courseId,
      courseCondition,
      researchCondition
    }) {
      const dataSource = this.mergeDataSourceHandle(courseCondition, researchCondition);
      const result = await services.research.getDimensionTable(
        { rows, columns, relationFilterVal, relationFilterName, dataSource },
        courseId
      );
      return result || {};
    },
    chartTapHandle(params) {
      if (params.componentType !== 'series') {
        return;
      }
      this.$emit('chartTap', params);
    },
    xAxisOver(params) {
      if (['xAxis', 'yAxis'].includes(params.componentType)) {
        const offsetX = params.event.offsetX + 10;
        const offsetY = params.event.offsetY - 50;
        this.chartEl.setOption({
          tooltip: {
            alwaysShowContent: true,
            formatter: () => params.value
          }
        });

        this.chartEl.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: 0,
          position: [offsetX, offsetY]
        });
      }
    },
    xAxisout(params) {
      if (['xAxis', 'yAxis'].includes(params.componentType)) {
        this.chartEl.setOption({
          tooltip: {
            alwaysShowContent: false,
            formatter: this.tooltipFormatter
          }
        });
      }
    },
    setOptionHandle(config) {
      if (this.chartEl) {
        this.chartEl.clear();
        this.chartEl.setOption(config);
        this.resize();
      }
    },
    resize() {
      if (this.chartEl) {
        this.chartEl.resize();
      }
    },
    changeThemeHandle(params, options) {
      if (this.chartEl) {
        this.chartEl.clear();
        this.chartEl.dispose();
        this.chartEl = null;
      }
      return this.refresh(params, options);
    },
    fetchBgStyle(index) {
      const colorsCount = this.curThemeColors.length;
      if (!colorsCount) {
        return {};
      }
      return { backgroundColor: this.curThemeColors[index % colorsCount] };
    },
    sortDataHandle(params, relationStatisticItem) {
      let sortStatistic = params.sortStatistic;
      let rowItem = null;
      if (Object.keys(sortStatistic || {}).length < 1) {
        rowItem = params.rows.find(item => {
          return ['num', 'date'].includes(item.type);
        });
        if (rowItem) {
          sortStatistic = { name: rowItem.title, type: 'asc' };
        } else if (params.statistics.length > 0) {
          let name = fetchStatisticValKey(params.statistics[0], relationStatisticItem);
          const statisticItem = params.statistics[0];
          if (params.rows.length === 0 && statisticItem.value === 'row_count') {
            name = `有${statisticItem.fieldDetail.title}`;
          }
          sortStatistic = { name, type: 'desc' };
        } else {
          sortStatistic = { name: relationStatisticItem.statisticName, type: 'desc' };
        }
      }

      let rowsData = [...params.originData.rowsData];
      if (!sortStatistic || sortStatistic.type === 'none') {
        return rowsData;
      }

      if (!rowItem) {
        rowItem = params.rows.find(item => item.title === sortStatistic.name);
      }

      if (rowItem && ['num', 'date'].includes(rowItem.type)) {
        let newDataArr = rowsData.map(rowData => {
          let sortVal = 0;
          let val = rowData[sortStatistic.name];
          if (rowItem.type === 'num') {
            if (val !== emptyColumnText) {
              val = String(val || '');
              const preNum = val.split('-')[0];
              sortVal = !preNum || preNum === '∞' ? 0 : Number(preNum);
            }
          }
          if (rowItem.type === 'date') {
            sortVal = Number(
              String(val)
                .split('-')
                .map(num => (num.length < 2 ? `0${num}` : num))
                .join('')
            );
          }
          return { rowData, sortVal };
        });
        newDataArr = sortBy(newDataArr, 'sortVal');
        rowsData = newDataArr.map(item => item.rowData);
      } else {
        rowsData = sortBy(rowsData, sortStatistic.name);
      }

      if (sortStatistic.type === 'desc') {
        rowsData.reverse();
      }
      const newRowsData = [];
      const unEffRowsData = [];
      rowsData.forEach(rowItem => {
        for (let i = 0; i < params.rows.length; i++) {
          const name = params.rows[i].title;
          if (rowItem[name] === emptyColumnText) {
            unEffRowsData.push(rowItem);
            return;
          }
        }
        newRowsData.push(rowItem);
      });
      return [...newRowsData, ...unEffRowsData];
    }
  }
};
